import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Languages, Clock4, HeartHandshake } from "lucide-react";

export default function AboutPage() {
  const { t, lang } = useLanguage();

  const values = [
    {
      icon: ShieldCheck,
      title: lang === "es" ? "Honestidad" : "Honesty",
      text:
        lang === "es"
          ? "Te decimos la verdad sobre tu situación fiscal, aunque no sea lo que quieras oír."
          : "We tell you the truth about your tax situation — even when it's not what you want to hear.",
    },
    {
      icon: Languages,
      title: lang === "es" ? "Bilingüe" : "Bilingual",
      text:
        lang === "es"
          ? "Atendemos en español e inglés con el mismo nivel de profesionalismo."
          : "We serve you in Spanish and English with the same level of professionalism.",
    },
    {
      icon: Clock4,
      title: lang === "es" ? "Puntualidad" : "On time",
      text:
        lang === "es"
          ? "Respondemos rápido, cumplimos fechas y nunca te dejamos en visto."
          : "Quick responses, deadlines met, and we never leave you hanging.",
    },
    {
      icon: HeartHandshake,
      title: lang === "es" ? "Cercanía" : "Personal touch",
      text:
        lang === "es"
          ? "Eres una persona, no un número. Recordamos tu historia."
          : "You're a person, not a number. We remember your story.",
    },
  ];

  return (
    <div data-testid="about-page">
      <section className="py-24">
        <div className="container-pad grid gap-14 lg:grid-cols-[1.1fr_1fr] items-center">
          <div>
            <div className="overline">{t.about.overline}</div>
            <h1 className="mt-4 font-serif text-5xl sm:text-6xl leading-[1] text-brand-primary">
              {t.about.title}
            </h1>
            <p className="mt-7 text-lg text-brand-text/75 leading-relaxed">{t.about.p1}</p>
            <p className="mt-5 text-lg text-brand-text/75 leading-relaxed">{t.about.p2}</p>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-surface px-7 py-4 text-sm font-semibold hover:bg-brand-secondary transition-colors"
            >
              {t.nav.cta}
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-5 rounded-3xl bg-brand-subtle rotate-2" aria-hidden />
            <img
              src="/julio.jpg"
              alt="Julio Rodríguez"
              className="relative w-full h-[560px] object-cover rounded-2xl shadow-xl"
              style={{ objectPosition: "center 15%" }}
            />
            <div className="absolute -bottom-6 left-6 lg:left-10 bg-brand-surface border border-brand-line rounded-xl px-6 py-4 shadow-lg">
              <div className="overline text-brand-accent">{t.about.caption_label}</div>
              <div className="mt-1.5 font-serif text-xl text-brand-primary">Julio Rodríguez</div>
              <div className="text-xs text-brand-muteText mt-0.5">{t.about.caption_role}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-subtle">
        <div className="container-pad grid grid-cols-2 lg:grid-cols-4 gap-6">
          {t.about.stats.map((s) => (
            <div
              key={s.label}
              className="rounded-xl bg-brand-surface border border-brand-line p-7"
              data-testid={`stat-${s.label}`}
            >
              <div className="font-serif text-4xl sm:text-5xl text-brand-primary">{s.value}</div>
              <div className="mt-2 text-sm text-brand-muteText uppercase tracking-widest">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24">
        <div className="container-pad">
          <div className="max-w-2xl">
            <div className="overline">{lang === "es" ? "Nuestros valores" : "Our values"}</div>
            <h2 className="mt-3 text-4xl sm:text-5xl leading-[1.05]">
              {lang === "es"
                ? "Cómo trabajamos con cada cliente"
                : "How we work with every client"}
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-xl bg-brand-surface border border-brand-line p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                data-testid={`value-${v.title}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-subtle text-brand-accent">
                  <v.icon size={22} />
                </div>
                <h3 className="mt-5 font-serif text-xl text-brand-primary">{v.title}</h3>
                <p className="mt-2 text-sm text-brand-text/70 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
